import "../../App.css";
import "./DepositRequest.css";

import {
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    useSearchParams,
} from "react-router-dom";
import {
    Image,
    Input,
    Table,
} from "antd";
import axios from "axios";

import Appconfig from "../../config/config";
import "react-notifications/lib/notifications.css";
import {
    NotificationContainer,
    NotificationManager,
} from "react-notifications";
import {
    WebSocketContext,
} from "../../context/websocket";

const getLoggedInUser = () => {
    try {
        const storedUser =
            localStorage.getItem("userdata");

        return storedUser
            ? JSON.parse(storedUser)
            : {};
    } catch (error) {
        console.error(
            "Unable to read logged-in user:",
            error
        );

        return {};
    }
};

const formatDate = (value) => {
    if (!value) {
        return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "-";
    }

    return date.toLocaleString();
};

const formatAmount = (value) => {
    const amount = Number(value);

    if (!Number.isFinite(amount)) {
        return "0.00";
    }

    return amount.toFixed(2);
};

const VALID_DASHBOARD_FILTERS = [
    "pending",
    "approved",
    "rejected",
    "all",
];

const normalizeRequestStatus = (status) => {
    return String(status || "")
        .trim()
        .toLowerCase();
};

const isPendingStatus = (status) => {
    return [
        "request",
        "process",
        "pending",
    ].includes(normalizeRequestStatus(status));
};

const isApprovedStatus = (status) => {
    return [
        "confirm",
        "confirmed",
        "approved",
    ].includes(normalizeRequestStatus(status));
};

const isRejectedStatus = (status) => {
    return [
        "reject",
        "rejected",
        "cancel",
        "cancelled",
        "canceled",
    ].includes(normalizeRequestStatus(status));
};

const isRecordWithinRange = (
    record,
    fromDate,
    toDate
) => {
    if (!fromDate && !toDate) {
        return true;
    }

    const recordDate = new Date(
        record?.createdAt ||
        record?.created_at ||
        0
    );

    if (Number.isNaN(recordDate.getTime())) {
        return false;
    }

    const parsedFromDate = fromDate
        ? new Date(fromDate)
        : null;

    const parsedToDate = toDate
        ? new Date(toDate)
        : null;

    if (
        parsedFromDate &&
        !Number.isNaN(parsedFromDate.getTime()) &&
        recordDate < parsedFromDate
    ) {
        return false;
    }

    if (
        parsedToDate &&
        !Number.isNaN(parsedToDate.getTime()) &&
        recordDate > parsedToDate
    ) {
        return false;
    }

    return true;
};

const normalizeStatus = (status) => {
    const normalized = String(
        status || ""
    ).trim().toLowerCase();

    if (
        [
            "confirm",
            "confirmed",
            "approved",
            "approve",
        ].includes(normalized)
    ) {
        return "approved";
    }

    if (
        [
            "reject",
            "rejected",
            "cancel",
            "cancelled",
            "canceled",
        ].includes(normalized)
    ) {
        return "rejected";
    }

    if (
        [
            "request",
            "pending",
            "process",
            "processing",
        ].includes(normalized)
    ) {
        return "pending";
    }

    return normalized;
};

function DepositRequest() {
    const websocket =
        useContext(WebSocketContext);

    const [searchParams] = useSearchParams();

    const pendingSectionRef = useRef(null);
    const historySectionRef = useRef(null);

    const requestedFilter = String(
        searchParams.get("status") || "all"
    ).toLowerCase();

    const activeFilter =
        VALID_DASHBOARD_FILTERS.includes(
            requestedFilter
        )
            ? requestedFilter
            : "all";

    const dashboardFromDate =
        searchParams.get("from_date");

    const dashboardToDate =
        searchParams.get("to_date");

    const userInfo = getLoggedInUser();

    const [
        depositRequest,
        setDepositRequest,
    ] = useState([]);

    const [
        depositHistory,
        setDepositHistory,
    ] = useState([]);

    const [remarks, setRemarks] =
        useState({});

    const [
        processingRequestId,
        setProcessingRequestId,
    ] = useState("");

    const [requestsLoading, setRequestsLoading] =
        useState(false);

    const [historyLoading, setHistoryLoading] =
        useState(false);

    const filteredDepositRequests = useMemo(() => {
        /*
         * Pending/action table sirf Pending aur All
         * dashboard cards par required hai.
         */
        if (
            activeFilter !== "pending" &&
            activeFilter !== "all"
        ) {
            return [];
        }

        /*
         * getDepositRequest API already pending requests
         * return karti hai. Isliye additional date/status
         * filtering se valid row remove nahi karenge.
         */
        return depositRequest;
    }, [
        depositRequest,
        activeFilter,
    ]);

    const filteredDepositHistory = useMemo(() => {
        /*
         * Pending aur All case mein complete Transaction
         * History show hogi.
         */
        if (
            activeFilter === "pending" ||
            activeFilter === "all"
        ) {
            return depositHistory;
        }

        /*
         * Approved card par sirf approved records.
         */
        if (activeFilter === "approved") {
            return depositHistory.filter(
                (record) =>
                    isApprovedStatus(record?.status)
            );
        }

        /*
         * Rejected card par rejected/cancelled records.
         */
        if (activeFilter === "rejected") {
            return depositHistory.filter(
                (record) =>
                    isRejectedStatus(record?.status)
            );
        }

        return depositHistory;
    }, [
        depositHistory,
        activeFilter,
    ]);

    const showPendingSection =
        activeFilter === "pending" ||
        activeFilter === "all";

    /*
     * Transaction History har case mein show hogi.
     */
    const showHistorySection = true;

    const historyHeading = "Transaction History";

    const historyDescription =
        activeFilter === "approved"
            ? "Showing approved deposit transactions"
            : activeFilter === "rejected"
                ? "Showing rejected deposit transactions"
                : activeFilter === "pending"
                    ? "Complete deposit transaction history"
                    : "Completed and rejected deposit requests";

    const updateRemark = (
        requestId,
        value
    ) => {
        setRemarks((previous) => ({
            ...previous,
            [requestId]: value,
        }));
    };

    const removeRemark = (requestId) => {
        setRemarks((previous) => {
            const updatedRemarks = {
                ...previous,
            };

            delete updatedRemarks[requestId];

            return updatedRemarks;
        });
    };

    const getDepositRequest = async () => {
        if (!userInfo?._id) {
            setDepositRequest([]);
            return;
        }

        setRequestsLoading(true);

        try {
            const response = await axios.post(
                `${Appconfig.apiUrl}deposit/getDepositRequest`,
                {
                    site_code:
                        Appconfig.sitecodes,
                    user_id: userInfo._id,
                },
                {
                    headers: {
                        "Content-Type":
                            "application/json",
                    },
                }
            );

            if (response?.data?.result) {
                setDepositRequest(
                    Array.isArray(
                        response.data.resultData
                    )
                        ? response.data
                            .resultData
                        : []
                );
            } else {
                setDepositRequest([]);

                NotificationManager.error(
                    response?.data
                        ?.resultMessage ||
                    "Unable to load deposit requests.",
                    "",
                    3000
                );
            }
        } catch (error) {
            console.error(
                "Deposit request error:",
                error
            );

            setDepositRequest([]);

            NotificationManager.error(
                error?.response?.data
                    ?.resultMessage ||
                error?.message ||
                "Unable to load deposit requests.",
                "",
                3000
            );
        } finally {
            setRequestsLoading(false);
        }
    };

    const getDepositHistory = async () => {
        if (!userInfo?._id) {
            setDepositHistory([]);
            return;
        }

        setHistoryLoading(true);

        try {
            const response = await axios.post(
                `${Appconfig.apiUrl}deposit/getDepositHistory`,
                {
                    site_code:
                        Appconfig.sitecodes,
                    master_id: userInfo._id,
                },
                {
                    headers: {
                        "Content-Type":
                            "application/json",
                    },
                }
            );

            if (response?.data?.result) {
                setDepositHistory(
                    Array.isArray(
                        response.data.resultData
                    )
                        ? response.data
                            .resultData
                        : []
                );
            } else {
                setDepositHistory([]);

                NotificationManager.error(
                    response?.data
                        ?.resultMessage ||
                    "Unable to load deposit history.",
                    "",
                    3000
                );
            }
        } catch (error) {
            console.error(
                "Deposit history error:",
                error
            );

            setDepositHistory([]);

            NotificationManager.error(
                error?.response?.data
                    ?.resultMessage ||
                error?.message ||
                "Unable to load deposit history.",
                "",
                3000
            );
        } finally {
            setHistoryLoading(false);
        }
    };

    const sendBalanceUpdate = () => {
        if (
            websocket &&
            websocket.readyState === 1
        ) {
            websocket.send(
                JSON.stringify({
                    action:
                        "USER_BLOCK_UPDATE",
                    data: {
                        user_id:
                            userInfo?._id,
                    },
                })
            );
        }
    };

    const confirmDepositRequest =
        async (record, status) => {
            if (!record?._id) {
                NotificationManager.error(
                    "Invalid deposit request.",
                    "",
                    3000
                );

                return;
            }

            setProcessingRequestId(
                record._id
            );

            try {
                const response =
                    await axios.post(
                        `${Appconfig.apiUrl}deposit/confirmDeposit`,
                        {
                            record,
                            status,
                            master_details:
                                userInfo,
                            remark:
                                remarks[
                                record._id
                                ] || "",
                        },
                        {
                            headers: {
                                "Content-Type":
                                    "application/json",
                            },
                        }
                    );

                if (response?.data?.result) {
                    NotificationManager.success(
                        response?.data
                            ?.resultMessage ||
                        `Deposit request ${status.toLowerCase()}ed successfully.`,
                        "",
                        3000
                    );

                    removeRemark(record._id);

                    await Promise.all([
                        getDepositRequest(),
                        getDepositHistory(),
                    ]);

                    sendBalanceUpdate();
                } else {
                    NotificationManager.error(
                        response?.data
                            ?.resultMessage ||
                        "Unable to update the deposit request.",
                        "",
                        3000
                    );
                }
            } catch (error) {
                console.error(
                    "Confirm deposit error:",
                    error
                );

                NotificationManager.error(
                    error?.response?.data
                        ?.resultMessage ||
                    error?.message ||
                    "Unable to update the deposit request.",
                    "",
                    3000
                );
            } finally {
                setProcessingRequestId("");
            }
        };

    useEffect(() => {
        getDepositRequest();
        getDepositHistory();

        // Initial page request only
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (requestsLoading || historyLoading) {
            return;
        }

        const scrollTimer = window.setTimeout(
            () => {
                if (activeFilter === "pending") {
                    pendingSectionRef.current
                        ?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });

                    return;
                }

                if (
                    activeFilter === "approved" ||
                    activeFilter === "rejected"
                ) {
                    historySectionRef.current
                        ?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });
                }
            },
            100
        );

        return () => {
            window.clearTimeout(scrollTimer);
        };
    }, [
        activeFilter,
        requestsLoading,
        historyLoading,
    ]);

    const renderScreenshot = (record) => {
        if (!record?.screenshot_name) {
            return (
                <span className="deposit-no-proof">
                    No screenshot
                </span>
            );
        }

        return (
            <Image
                width={52}
                height={52}
                preview
                className="deposit-proof-image"
                src={`${Appconfig.apiUrl}deposit/${record.screenshot_name}`}
            />
        );
    };

    const requestColumns = [
        {
            title: "User Name",
            key: "userName",
            fixed: "left",
            width: 130,
            sorter: (first, second) =>
                String(
                    first?.user_name || ""
                ).localeCompare(
                    String(
                        second?.user_name ||
                        ""
                    )
                ),
            render: (_, record) => (
                <span className="deposit-primary-value">
                    {record?.user_name ||
                        "-"}
                </span>
            ),
        },
        {
            title: "Type",
            key: "type",
            width: 100,
            sorter: (first, second) =>
                String(
                    first?.type || ""
                ).localeCompare(
                    String(
                        second?.type || ""
                    )
                ),
            render: (_, record) => (
                <span className="deposit-type-badge">
                    {record?.type || "-"}
                </span>
            ),
        },
        {
            title: "Amount",
            key: "amount",
            width: 120,
            align: "right",
            sorter: (first, second) =>
                Number(
                    first?.amount || 0
                ) -
                Number(
                    second?.amount || 0
                ),
            render: (_, record) => (
                <span className="deposit-amount">
                    ₹{" "}
                    {formatAmount(
                        record?.amount
                    )}
                </span>
            ),
        },
        {
            title: "Screenshot",
            key: "screenshot",
            width: 105,
            render: (_, record) =>
                renderScreenshot(record),
        },
        {
            title: "UTR Number",
            key: "utrNumber",
            width: 145,
            sorter: (first, second) =>
                String(
                    first?.utr_number || ""
                ).localeCompare(
                    String(
                        second?.utr_number ||
                        ""
                    )
                ),
            render: (_, record) => (
                <span className="deposit-secondary-value">
                    {record?.utr_number ||
                        "-"}
                </span>
            ),
        },
        {
            title: "Remark",
            key: "remark",
            width: 190,
            render: (_, record) => (
                <Input
                    value={
                        remarks[
                        record?._id
                        ] || ""
                    }
                    placeholder="Enter remark"
                    className="deposit-remark-input"
                    disabled={
                        processingRequestId ===
                        record?._id
                    }
                    onChange={(event) =>
                        updateRemark(
                            record?._id,
                            event.target.value
                        )
                    }
                />
            ),
        },
        {
            title: "Request Date",
            key: "requestDate",
            width: 175,
            sorter: (first, second) =>
                new Date(
                    first?.createdAt || 0
                ).getTime() -
                new Date(
                    second?.createdAt || 0
                ).getTime(),
            render: (_, record) => (
                <span className="deposit-date">
                    {formatDate(
                        record?.createdAt
                    )}
                </span>
            ),
        },
        {
            title: "Action",
            key: "action",
            fixed: "right",
            width: 175,
            render: (_, record) => {
                const isProcessing =
                    processingRequestId ===
                    record?._id;

                return (
                    <div className="deposit-actions">
                        <button
                            type="button"
                            disabled={
                                Boolean(
                                    processingRequestId
                                )
                            }
                            className="deposit-confirm-button"
                            onClick={() =>
                                confirmDepositRequest(
                                    record,
                                    "Confirm"
                                )
                            }
                        >
                            {isProcessing
                                ? "Processing"
                                : "Confirm"}
                        </button>

                        <button
                            type="button"
                            disabled={
                                Boolean(
                                    processingRequestId
                                )
                            }
                            className="deposit-reject-button"
                            onClick={() =>
                                confirmDepositRequest(
                                    record,
                                    "Reject"
                                )
                            }
                        >
                            Reject
                        </button>
                    </div>
                );
            },
        },
    ];

    const historyColumns = [
        {
            title: "User Name",
            key: "userName",
            fixed: "left",
            width: 130,
            sorter: (first, second) =>
                String(
                    first?.user_name || ""
                ).localeCompare(
                    String(
                        second?.user_name ||
                        ""
                    )
                ),
            render: (_, record) => (
                <span className="deposit-primary-value">
                    {record?.user_name ||
                        "-"}
                </span>
            ),
        },
        {
            title: "Type",
            key: "type",
            width: 100,
            sorter: (first, second) =>
                String(
                    first?.type || ""
                ).localeCompare(
                    String(
                        second?.type || ""
                    )
                ),
            render: (_, record) => (
                <span className="deposit-type-badge">
                    {record?.type || "-"}
                </span>
            ),
        },

        ...(depositHistory.some(
            (record) => record?.bank_name
        )
            ? [
                {
                    title: "Bank Name",
                    key: "bankName",
                    width: 135,
                    render: (_, record) =>
                        record?.bank_name ||
                        "N/A",
                },
            ]
            : []),

        ...(depositHistory.some(
            (record) => record?.account_no
        )
            ? [
                {
                    title:
                        "Account Number",
                    key: "accountNumber",
                    width: 160,
                    render: (_, record) =>
                        record?.account_no ||
                        "N/A",
                },
            ]
            : []),

        ...(depositHistory.some(
            (record) => record?.ifsc_code
        )
            ? [
                {
                    title: "IFSC Code",
                    key: "ifscCode",
                    width: 125,
                    render: (_, record) =>
                        record?.ifsc_code ||
                        "N/A",
                },
            ]
            : []),

        ...(depositHistory.some(
            (record) => record?.upi_id
        )
            ? [
                {
                    title: "UPI ID",
                    key: "upiId",
                    width: 150,
                    render: (_, record) =>
                        record?.upi_id ||
                        "N/A",
                },
            ]
            : []),

        ...(depositHistory.some(
            (record) =>
                record?.holder_name
        )
            ? [
                {
                    title: "Holder Name",
                    key: "holderName",
                    width: 145,
                    render: (_, record) =>
                        record?.holder_name ||
                        "N/A",
                },
            ]
            : []),

        {
            title: "Remarks",
            key: "remarks",
            width: 170,
            render: (_, record) => (
                <span className="deposit-secondary-value">
                    {record?.remark || "-"}
                </span>
            ),
        },
        {
            title: "Amount",
            key: "amount",
            width: 120,
            align: "right",
            sorter: (first, second) =>
                Number(
                    first?.amount || 0
                ) -
                Number(
                    second?.amount || 0
                ),
            render: (_, record) => (
                <span className="deposit-amount">
                    ₹{" "}
                    {formatAmount(
                        record?.amount
                    )}
                </span>
            ),
        },
        {
            title: "Screenshot",
            key: "screenshot",
            width: 105,
            render: (_, record) =>
                renderScreenshot(record),
        },
        {
            title: "UTR Number",
            key: "utrNumber",
            width: 145,
            render: (_, record) => (
                <span className="deposit-secondary-value">
                    {record?.utr_number ||
                        "-"}
                </span>
            ),
        },
        {
            title: "Request Date",
            key: "requestDate",
            width: 175,
            sorter: (first, second) =>
                new Date(
                    first?.createdAt || 0
                ).getTime() -
                new Date(
                    second?.createdAt || 0
                ).getTime(),
            render: (_, record) => (
                <span className="deposit-date">
                    {formatDate(
                        record?.createdAt
                    )}
                </span>
            ),
        },
        {
            title: "Status",
            key: "status",
            fixed: "right",
            width: 105,
            sorter: (first, second) =>
                String(
                    first?.status || ""
                ).localeCompare(
                    String(
                        second?.status ||
                        ""
                    )
                ),
            render: (_, record) => {
                const status =
                    String(
                        record?.status ||
                        "Pending"
                    );

                const normalizedStatus =
                    status.toLowerCase();

                const statusClass =
                    normalizedStatus ===
                        "confirm" ||
                        normalizedStatus ===
                        "confirmed" ||
                        normalizedStatus ===
                        "approved"
                        ? "approved"
                        : normalizedStatus ===
                            "reject" ||
                            normalizedStatus ===
                            "rejected"
                            ? "rejected"
                            : "pending";

                return (
                    <span
                        className={`deposit-status ${statusClass}`}
                    >
                        {status}
                    </span>
                );
            },
        },
    ];

    return (
        <>
            <NotificationContainer />

            <main className="deposit-page">
                <section className="deposit-overview">
                    <div className="deposit-page-heading">
                        <div className="deposit-heading-content">
                            <span className="deposit-heading-icon">
                                ↓
                            </span>

                            <div>
                                <h1>
                                    Deposit
                                    Management
                                </h1>

                                <p>
                                    Review pending
                                    requests and
                                    deposit history
                                </p>
                            </div>
                        </div>

                        <div className="deposit-overview-counts">
                            <div className="deposit-count pending">
                                <span>
                                    Pending
                                    Requests
                                </span>

                                <strong>
                                    {
                                        filteredDepositRequests.length
                                    }
                                </strong>
                            </div>

                            <div className="deposit-count history">
                                <span>
                                    History
                                    Records
                                </span>

                                <strong>
                                    {
                                        filteredDepositHistory.length
                                    }
                                </strong>
                            </div>
                        </div>
                    </div>
                </section>

                {showPendingSection && (
                    <section
                        ref={pendingSectionRef}
                        className="deposit-card"
                    >
                        <div className="deposit-card-heading">
                            <div>
                                <h2>
                                    Pending Deposit
                                    Requests
                                </h2>

                                <p>
                                    Confirm or reject
                                    pending user
                                    deposits
                                </p>
                            </div>

                            <span className="deposit-heading-count">
                                {
                                    filteredDepositRequests.length
                                }
                            </span>
                        </div>

                        <div className="deposit-table-wrapper">
                            <Table
                                rowKey={(record) =>
                                    record?._id
                                }
                                columns={
                                    requestColumns
                                }
                                dataSource={
                                    filteredDepositRequests
                                }
                                className="deposit-table"
                                loading={
                                    requestsLoading
                                }
                                pagination={false}
                                locale={{
                                    emptyText:
                                        "No pending deposit requests found.",
                                }}
                                scroll={{
                                    x: 1150,
                                }}
                            />
                        </div>
                    </section>
                )}

                {showHistorySection && (
                    <section
                        ref={historySectionRef}
                        className="deposit-card"
                    >
                        <div className="deposit-card-heading">
                            <div>
                                {/* <h2>
                                    Transaction
                                    History
                                </h2> */}
                                <h2>{historyHeading}</h2>

                                {/* <p>
                                    Completed and
                                    rejected deposit
                                    requests
                                </p> */}
                                <p>{historyDescription}</p>
                            </div>

                            <span className="deposit-heading-count">
                                {
                                    filteredDepositHistory.length
                                }
                            </span>
                        </div>

                        <div className="deposit-table-wrapper">
                            <Table
                                rowKey={(record) =>
                                    record?._id
                                }
                                columns={
                                    historyColumns
                                }
                                dataSource={
                                    filteredDepositHistory
                                }
                                className="deposit-table deposit-history-table"
                                loading={
                                    historyLoading
                                }
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: false,
                                    hideOnSinglePage: true,
                                }}
                                locale={{
                                    emptyText:
                                        activeFilter === "approved"
                                            ? "No approved deposit transactions found."
                                            : activeFilter === "rejected"
                                                ? "No rejected deposit transactions found."
                                                : "No deposit history found.",
                                }}
                                scroll={{
                                    x: 1250,
                                }}
                            />
                        </div>
                    </section>
                )}
            </main>
        </>
    );
}

export default DepositRequest;